/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { Switch } from "@/components/ui/switch";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowPathIcon, ClockIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { getSchedules } from "@/api/speedtest";
import { getApiUrl } from "@/utils/baseUrl";
import { Schedule } from "@/types/types";
import { showToast } from "@/components/common/Toast";
import { convertUserTimeToUTC, formatDateWithSettings, formatTimeWithSettings, getTimeFormatSettings } from "@/utils/timeSettings";
import { cn } from "@/lib/utils";

/** "exact:" intervals hold daily UTC times; show them in the user's timezone. */
function utcTime(hhmm: string): string {
  const [h, m] = hhmm.trim().split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const d = new Date();
  d.setUTCHours(h, m, 0, 0);
  return formatTimeWithSettings(d);
}

function describe(schedule: Schedule): string {
  if (!schedule.interval.startsWith("exact:")) return `Every ${schedule.interval}`;
  const times = schedule.interval.slice(6).split(",").map(utcTime);
  return `Daily at ${times.join(", ")}`;
}

function serverLabel(schedule: Schedule): string {
  const ids = schedule.serverIds ?? [];
  if (ids.length === 0) return "Automatic server";
  const first = ids[0];
  const name = schedule.options?.serverName;
  if (first.startsWith("iperf3-")) return `${name || first.slice(7)} · iperf3`;
  return ids.length > 1 ? `${ids.length} servers` : `Server ${first}`;
}

function nextRun(schedule: Schedule): string {
  if (!schedule.enabled) return "paused";
  if (!schedule.nextRun) return "—";
  const d = new Date(schedule.nextRun);
  if (Number.isNaN(d.getTime())) return "—";
  const same = d.toDateString() === new Date().toDateString();
  return same
    ? `next ${formatTimeWithSettings(d)}`
    : `next ${formatDateWithSettings(d, { day: "numeric", month: "short" })}, ${formatTimeWithSettings(d)}`;
}

const INTERVALS: [string, string][] = [
  ["5m", "Every 5 minutes"],
  ["15m", "Every 15 minutes"],
  ["30m", "Every 30 minutes"],
  ["1h", "Every hour"],
  ["6h", "Every 6 hours"],
  ["12h", "Every 12 hours"],
  ["24h", "Every day"],
  ["7d", "Every week"],
];

interface Props {
  /** Opens the speed test page, where a specific server can be picked. */
  onManage: () => void;
}

export const ScheduleOverview: React.FC<Props> = ({ onManage }) => {
  const queryClient = useQueryClient();

  const { data: schedules = [], isError } = useQuery<Schedule[]>({
    queryKey: ["schedules"],
    queryFn: async () => (await getSchedules()) ?? [],
    refetchInterval: 30_000,
  });

  const toggle = useMutation({
    mutationFn: async (schedule: Schedule) => {
      const res = await fetch(getApiUrl(`/schedules/${schedule.id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...schedule, enabled: !schedule.enabled }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["schedules"] }),
    onError: (e: Error) => showToast("Could not update the schedule", "error", { description: e.message }),
  });

  // ---- adding a schedule right here (automatic server) ----
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<"interval" | "exact">("interval");
  const [every, setEvery] = useState("6h");
  const [times, setTimes] = useState<string[]>(["09:00"]);

  const create = useMutation({
    mutationFn: async () => {
      const tzSetting = getTimeFormatSettings().timezone;
      const tz = tzSetting === "auto" ? Intl.DateTimeFormat().resolvedOptions().timeZone : tzSetting;
      const interval = kind === "exact" ? `exact:${times.map((x) => convertUserTimeToUTC(x, tz)).join(",")}` : every;
      const res = await fetch(getApiUrl("/schedules"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          serverIds: [],
          interval,
          enabled: true,
          options: { enableDownload: true, enableUpload: true, serverIds: [], useIperf: false, useLibrespeed: false },
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.message ?? `HTTP ${res.status}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
      showToast("Schedule created", "success");
      setAdding(false);
    },
    onError: (e: Error) => showToast("Could not create the schedule", "error", { description: e.message }),
  });

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(getApiUrl(`/schedules/${id}`), { method: "DELETE" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedules"] });
      showToast("Schedule deleted", "success");
    },
    onError: (e: Error) => showToast("Could not delete the schedule", "error", { description: e.message }),
  });

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-5 dark:border-white/10 dark:bg-white/[0.03]">
      <div className="mb-3 flex items-center gap-3">
        <div>
          <h3 className="text-[15px] font-semibold text-gray-900 dark:text-white">Speed test schedule</h3>
          <p className="text-[12px] text-gray-500 dark:text-gray-400">
            {schedules.length > 0
              ? `${schedules.filter((s) => s.enabled).length} of ${schedules.length} active`
              : "tests that run on their own"}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          aria-expanded={adding}
          className="ml-auto inline-flex h-8 flex-none items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 px-3 text-[12.5px] text-gray-600 hover:bg-gray-100 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          Add schedule
        </button>
      </div>

      {adding && (
        <div className="mb-4 space-y-3 rounded-lg border border-gray-200 p-3 dark:border-white/10">
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 text-[12.5px] dark:border-white/10" role="tablist">
            {(["interval", "exact"] as const).map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={kind === k}
                onClick={() => setKind(k)}
                className={cn("rounded-md px-3 py-1", kind === k ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900" : "text-gray-600 dark:text-gray-300")}
              >
                {k === "interval" ? "Interval" : "Exact time"}
              </button>
            ))}
          </div>

          {kind === "interval" ? (
            <select
              value={every}
              onChange={(e) => setEvery(e.target.value)}
              className="h-9 w-full rounded-lg border border-gray-200 bg-transparent px-2 text-[13px] text-gray-900 dark:border-white/10 dark:text-gray-100 dark:[&>option]:bg-gray-900"
            >
              {INTERVALS.map(([v, label]) => (
                <option key={v} value={v}>
                  {label}
                </option>
              ))}
            </select>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {times.map((tm, i) => (
                <span key={i} className="inline-flex items-center gap-1">
                  <input
                    type="time"
                    value={tm}
                    onChange={(e) => setTimes((cur) => cur.map((x, j) => (j === i ? e.target.value : x)))}
                    className="h-9 rounded-lg border border-gray-200 bg-transparent px-2 text-[13px] text-gray-900 dark:border-white/10 dark:text-gray-100"
                  />
                  {times.length > 1 && (
                    <button type="button" aria-label="Remove time" onClick={() => setTimes((cur) => cur.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  )}
                </span>
              ))}
              <button
                type="button"
                onClick={() => setTimes((cur) => [...cur, "18:00"])}
                className="h-9 rounded-lg border border-dashed border-gray-300 px-3 text-[12.5px] text-gray-600 dark:border-white/20 dark:text-gray-300"
              >
                + time
              </button>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={create.isPending || (kind === "exact" && times.some((x) => !x))}
              onClick={() => create.mutate()}
              className="h-9 rounded-lg bg-emerald-600 px-4 text-[13px] font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              Create schedule
            </button>
            <span className="text-[12px] text-gray-500 dark:text-gray-400">uses the nearest server automatically</span>
            <button type="button" onClick={onManage} className="ml-auto text-[12px] text-blue-600 hover:underline dark:text-blue-400">
              Choose a specific server
            </button>
          </div>
        </div>
      )}

      {schedules.length === 0 ? (
        <div className="flex flex-col items-center gap-1 rounded-lg border border-dashed border-gray-300 px-4 py-6 text-center dark:border-white/15">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {isError ? "Couldn't load schedules." : "No schedules yet."}
          </p>
          {!isError && (
            <p className="text-[12px] text-gray-500 dark:text-gray-400">
              Schedule a test every few hours to build the speed history without lifting a finger.
            </p>
          )}
        </div>
      ) : (
        <ul className="divide-y divide-gray-200 dark:divide-white/10">
          {schedules.map((s) => (
            <li key={s.id} className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
              {s.interval.startsWith("exact:") ? (
                <ClockIcon className="h-4 w-4 flex-none text-gray-500 dark:text-gray-400" />
              ) : (
                <ArrowPathIcon className="h-4 w-4 flex-none text-gray-500 dark:text-gray-400" />
              )}
              <div className="min-w-0 flex-1">
                <div className={cn("truncate text-[13px] font-medium", s.enabled ? "text-gray-900 dark:text-gray-100" : "text-gray-500 line-through dark:text-gray-500")}>
                  {describe(s)}
                </div>
                <div className="truncate text-[11.5px] text-gray-500 dark:text-gray-400">
                  <span>{serverLabel(s)}</span> · <span>{nextRun(s)}</span>
                </div>
              </div>
              <Switch
                size="sm"
                checked={s.enabled}
                aria-label={s.enabled ? "Pause schedule" : "Resume schedule"}
                disabled={toggle.isPending}
                onCheckedChange={() => toggle.mutate(s)}
              />
              <button
                type="button"
                aria-label="Delete schedule"
                title="Delete schedule"
                disabled={remove.isPending || !s.id}
                onClick={() => s.id && remove.mutate(s.id)}
                className="flex h-7 w-7 flex-none items-center justify-center rounded-lg text-gray-400 hover:bg-red-500/10 hover:text-red-500 disabled:opacity-60"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
